import { BooLogo } from "@/components/BooLogo";
import { Button } from "@/components/ui/button";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { authClient, useSession } from "@/lib/auth-client";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

// Landing route for the waitlist-approval email's "Finish setup" CTA
// (/finish-setup?email=<approved address>). It exists to carry the approved
// identity through the door:
//
//   - signed out            → /login with the approved email prefilled +
//                             locked, next=/onboarding
//   - signed in, same email → /onboarding (GatedShell resolves the step)
//   - signed in, other acct → "wrong account" screen with a one-click
//                             switch (sign out → /login prefilled)
//
// Without this the email pointed at the bare root, which dumped an
// already-signed-in user onto *that* account's dashboard and left a
// signed-out one on an empty login form.
export function FinishSetupPage() {
  useDocumentTitle("Finish setup");
  const navigate = useNavigate();
  const { email: linkEmail } = useSearch({ strict: false }) as { email?: string };
  const { data: session, isPending } = useSession();
  const [switching, setSwitching] = useState(false);

  const sessionEmail = session?.user?.email ?? null;
  const mismatch =
    !!sessionEmail && !!linkEmail && sessionEmail.toLowerCase() !== linkEmail.toLowerCase();

  useEffect(() => {
    if (isPending) return;
    if (!sessionEmail) {
      navigate({
        to: "/login",
        search: linkEmail ? { next: "/onboarding", email: linkEmail } : { next: "/onboarding" },
        replace: true,
      });
      return;
    }
    if (!mismatch) navigate({ to: "/onboarding", replace: true });
  }, [isPending, sessionEmail, mismatch, linkEmail, navigate]);

  async function switchAccount() {
    if (!linkEmail) return;
    setSwitching(true);
    try {
      await authClient.signOut();
    } finally {
      // Hard navigation so no stale session state (react-query caches, the
      // better-auth session atom) survives into the new identity.
      const params = new URLSearchParams({ next: "/onboarding", email: linkEmail });
      window.location.assign(`/login?${params.toString()}`);
    }
  }

  if (!mismatch) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="relative">
          <span
            className="boo-pulse absolute -inset-8 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(127, 119, 221, 0.4), transparent 70%)",
            }}
          />
          <BooLogo className="relative size-16 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-1 flex-col items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md flex flex-col items-center gap-4 text-center">
        <BooLogo className="size-12 rounded-xl" />
        <h1 className="text-2xl font-semibold tracking-tight">Wrong account</h1>
        <p className="text-sm text-muted-foreground">
          This setup link was sent to <strong className="text-foreground">{linkEmail}</strong>, but
          you're signed in as <strong className="text-foreground">{sessionEmail}</strong>.
        </p>
        <p className="text-sm text-muted-foreground">
          Sign in with <strong className="text-foreground">{linkEmail}</strong> to finish setting up
          your Boo.
        </p>
        <Button onClick={switchAccount} disabled={switching}>
          {switching ? "Switching…" : `Sign in as ${linkEmail}`}
        </Button>
        <Link
          to="/"
          className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Stay signed in as {sessionEmail}
        </Link>
      </div>
    </div>
  );
}
